import { Component, ElementRef, HostListener, Input, OnInit, OnDestroy } from '@angular/core';
import { NotificationsService } from 'app/shared/services-api-central/notifications/notifications.service';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { NotificationItem } from 'app/shared/services-api-central/notifications/notifications.interface';
import { Observable, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ReleaseNotesService } from 'app/shared/services-api-central/release-notes/release-notes.service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationClearPopupComponent } from '../notification-clear-popup/notification-clear-popup.component';
import { EThemeName, ThemeService } from '@wind-log_library/fe-component-library';

@Component({
    selector: 'app-bell-switch',
    templateUrl: './bell-switch.component.html',
    styleUrls: ['./bell-switch.component.scss'],
    animations: [
        trigger('fadeOut', [
            state('visible', style({ opacity: 1, height: '*' })),
            state('hidden', style({ opacity: 0, height: 0, margin: 0 })),
            transition('visible => hidden', [animate('300ms ease-in-out')]),
        ]),
    ],
    standalone: false
})
export class BellSwitchComponent implements OnInit, OnDestroy {
  @Input() smallView: boolean = false;

  public notificationList: NotificationItem[] = [];
  public isDropdownOpen = false;
  public unreadNotifications = 0;
  public theme$: Observable<EThemeName>;
  private unsubscriber = new ReplaySubject();

  constructor(
    public dialog: MatDialog,
    private notificationsService: NotificationsService,
    private elementRef: ElementRef,
    private releaseNotesService: ReleaseNotesService,
    private themeService: ThemeService,
  ) {}

  public ngOnInit(): void {
    this.theme$ = this.themeService.getTheme();
    this.notificationsService.notifications$.pipe(takeUntil(this.unsubscriber)).subscribe((notifications) => {
      this.notificationList = notifications;
      this.getUnreadNotifications();
    });
  }

  private getUnreadNotifications(): void {
    this.notificationList?.sort((a, b) => {
      if (a.isRead !== b.isRead) {
        return a.isRead ? 1 : -1;
      }
      const tsA = new Date(a.startTS).getTime();
      const tsB = new Date(b.startTS).getTime();
      return tsB - tsA;
    });
  
    this.unreadNotifications = this.notificationList?.filter(notification => !notification.isRead).length;
  }

  public toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  public updateAnimationState(item: NotificationItem): void {
    const foundItem = this.notificationList.find((i) => i._id === item._id);
    if (foundItem) {
      foundItem.animationState = item.isDeleted ? 'hidden' : 'visible';
    }
  }

  public markAsRead(id: string): void {
    this.notificationsService.markAsRead(id);

    const index = this.notificationList.findIndex((n) => n._id === id);
    if (index !== -1) {
      this.notificationList[index].isRead = true;
    }

    this.getUnreadNotifications();
  }

  public markAsDelete(id: string): void {
    this.notificationsService.markAsDeleted(id);

    setTimeout(() => {
      const index = this.notificationList.findIndex((i) => i._id === id);
      if (index !== -1) {
        this.notificationList.splice(index, 1);
      }
      this.getUnreadNotifications();
    }, 300);
  }

  private markAllAsDeleted(): void {
    this.notificationsService.markAllAsDeleted().subscribe(filteredNotifications => {
      this.notificationList = filteredNotifications;
    });

    this.getUnreadNotifications();
  }

  public clearAll(): void {
    this.dialog.closeAll();

    const dialogRef = this.dialog.open(NotificationClearPopupComponent, {
      data: {},
      enterAnimationDuration: '500ms',
      exitAnimationDuration: '500ms',
      width: '600px',
      panelClass: 'dialog-class'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.template) {
        const selectedExportTemplate = result.template;
        this.markAllAsDeleted();
      }
    });
  }

  public getItemAnimationState(item) {
    return item.isDeleted ? 'hidden' : 'visible';
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.isDropdownOpen = false;
    }
  }

  public openReleaseNotes() {
    this.releaseNotesService.open();
  }

  public ngOnDestroy(): void {
    if (this.unsubscriber) {
      this.unsubscriber.unsubscribe();
    }
  }
}
